import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	model,
	signal,
} from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';
import { SvgIconComponent } from 'angular-svg-icon';

import { CheckboxComponent } from '@valk-nx/components/ui-checkbox/src/lib/checkbox';
import { SelectionCardComponent } from '@valk-nx/components/ui-selection-card/src/lib/selection-card';
import type { SelectionCardInterface } from '@valk-nx/components/ui-selection-card/src/lib/selection-card.interface';
import { TagComponent } from '@valk-nx/components/ui-tag/src/lib/tag';
import { SlugifyPipe } from '@valk-nx/core/lib/pipes/slugify/slugify.pipe';

import type { FiltersInterface } from './contracts/filters.interface';
import { FilterNumericComponent } from './filter-numeric/filter-numeric';

interface FilterChipsInterface {
	group: string;
	name: string;
	value: string;
	type?: FiltersInterface['type'];
}

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: `vp-filters`,
	templateUrl: './filters.component.html',
	imports: [
		CheckboxComponent,
		FilterNumericComponent,
		SelectionCardComponent,
		SlugifyPipe,
		TagComponent,
		TranslatePipe,
		SvgIconComponent,
	],
})
export class FiltersComponent {
	protected readonly ITEMS_LIMIT = 8;

	filters = input<FiltersInterface[]>([]);

	activeFilters = model<Record<string, string[]>>({});

	protected expandedGroups = signal<Set<string>>(new Set());

	activeFilterChips = computed<FilterChipsInterface[]>(() => {
		const active = this.activeFilters();
		const groups = this.filters();

		return Object.entries(active).flatMap(([groupTitle, values]) => {
			const group = groups.find((g) => g.title === groupTitle);
			const items = group?.items ?? [];

			return values.map((value) => ({
				group: groupTitle,
				name: items.find((i) => i.value === value)?.name ?? value,
				value,
				type: group?.type,
			}));
		});
	});

	filteredFilters = computed<FiltersInterface[]>(() => {
		return this.filters().filter(
			(f) =>
				f.type === 'checkbox' ||
				f.type === 'layout' ||
				f.type === 'numeric',
		);
	});

	numericValues = computed<Record<string, string>>(() => {
		const active = this.activeFilters();

		return Object.fromEntries(
			this.filteredFilters()
				.filter((group) => group.type === 'numeric')
				.map((group) => [group.title, active[group.title]?.[0] ?? '']),
		);
	});

	layoutOptions = computed<Record<string, SelectionCardInterface[]>>(() => {
		const active = this.activeFilters();

		return Object.fromEntries(
			this.filteredFilters()
				.filter((group) => group.type === 'layout')
				.map((group) => [
					group.title,
					(group.items ?? []).map((item) => ({
						label: item.name,
						inputId: item.value,
						icon: item.icon
							? `assets/icons/layout-${item.icon}.svg`
							: undefined,
						selected:
							active[group.title]?.includes(item.value) ?? false,
					})),
				]),
		);
	});

	onLayoutOptionsChange(
		groupTitle: string,
		options: SelectionCardInterface[],
	): void {
		this.setGroupValues(
			groupTitle,
			options
				.filter((option) => option.selected)
				.map((option) => option.inputId),
		);
	}

	toggleShowMore(groupTitle: string): void {
		this.expandedGroups.update((groups) => {
			const next = new Set(groups);
			next.add(groupTitle);
			return next;
		});
	}

	clearAllFilters(): void {
		this.activeFilters.set({});
	}

	removeFilter(groupTitle: string, value: string): void {
		const groupValues = this.activeFilters()[groupTitle] ?? [];

		this.setGroupValues(
			groupTitle,
			groupValues.filter((v) => v !== value),
		);
	}

	setNumericValue(groupTitle: string, value: string): void {
		this.setGroupValues(groupTitle, value ? [value] : []);
	}

	setGroupValues(groupTitle: string, values: string[]): void {
		this.activeFilters.update((current) => {
			const next = { ...current };

			if (values.length > 0) {
				next[groupTitle] = values;
			} else {
				delete next[groupTitle];
			}

			return next;
		});
	}

	onFilterChange(groupTitle: string, itemValue: string, keep: boolean): void {
		const groupValues = this.activeFilters()[groupTitle] ?? [];

		this.setGroupValues(
			groupTitle,
			keep
				? [...new Set([...groupValues, itemValue])]
				: groupValues.filter((v) => v !== itemValue),
		);
	}
}
